/**
 * Pricing Section Component for Sigma08 Landing Page
 * IBM Carbon Design System implementation with educational pricing
 */

import React from 'react';
import {
  Grid,
  Column,
  Button,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Tag,
  Tile,
  Stack,
  Heading,
} from '@carbon/react';
import { ArrowRight, CheckmarkFilled, Star } from '@carbon/icons-react';
// import styles from './PricingSection.module.scss';

interface PricingSectionProps {
  variant?: 'default' | 'educator' | 'parent' | 'administrator';
  onPricingInteraction: (plan: string) => void;
}

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: {
    monthly: number;
    annual: number;
    perStudent?: number;
  };
  features: string[];
  limitations?: string[];
  highlight: boolean;
  bestFor: string[];
  educationalDiscount?: number;
  trialLength: number;
}

/**
 * Pricing Section Component
 * Displays educational pricing plans with variant-specific emphasis
 */
export const PricingSection: React.FC<PricingSectionProps> = ({ variant = 'default', onPricingInteraction }) => {
  // Educational pricing plans
  const pricingPlans: PricingPlan[] = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for individual educators or small classrooms',
      price: {
        monthly: 29,
        annual: 290,
        perStudent: 2,
      },
      features: [
        'Up to 30 students',
        'Basic learning analytics',
        'Assignment management',
        'Parent communication',
        'Mobile app access',
        'Email support',
      ],
      highlight: variant === 'educator',
      bestFor: ['Individual teachers', 'Small classrooms', 'Pilot programs'],
      educationalDiscount: 20,
      trialLength: 30,
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Comprehensive solution for schools and departments',
      price: {
        monthly: 89,
        annual: 890,
        perStudent: 5,
      },
      features: [
        'Up to 200 students',
        'Advanced analytics & reporting',
        'AI tutoring system',
        'Custom curriculum alignment',
        'Bulk parent communication',
        'Priority support',
        'Professional development resources',
        'Grade book integration',
      ],
      highlight: variant === 'default' || variant === 'administrator',
      bestFor: ['Schools', 'Departments', 'Growing districts'],
      educationalDiscount: 25,
      trialLength: 60,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Full-featured solution for large districts and institutions',
      price: {
        monthly: 0, // Custom pricing
        annual: 0,
        perStudent: 8,
      },
      features: [
        'Unlimited students',
        'District-wide analytics',
        'Custom integrations',
        'Advanced security & compliance',
        'Dedicated account manager',
        '24/7 premium support',
        'Custom training programs',
        'API access',
        'White-label options',
      ],
      highlight: variant === 'administrator',
      bestFor: ['Large districts', 'State education departments', 'International schools'],
      educationalDiscount: 30,
      trialLength: 90,
    },
  ];

  // Sort plans to show highlighted plan first
  const sortedPlans = [...pricingPlans].sort((a, b) => {
    if (a.highlight && !b.highlight) return -1;
    if (!a.highlight && b.highlight) return 1;
    return 0;
  });

  return (
    <section style={{ padding: '4rem 0', backgroundColor: '#ffffff' }}>
      <Grid>
        {/* Section Header - Using Carbon typography classes */}
        <Column lg={16} md={8} sm={4}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Heading style={{ fontSize: '3rem', fontWeight: 300, color: '#161616', marginBottom: '1rem' }}>
              Educational Pricing That Works for Everyone
            </Heading>
            <p
              style={{
                fontSize: '1.25rem',
                color: '#525252',
                maxWidth: '600px',
                margin: '0 auto 2rem',
                lineHeight: 1.6,
              }}
            >
              Transparent, fair pricing designed specifically for educational institutions. All plans include free
              trials and educational discounts.
            </p>

            {/* Educational Benefits Banner */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#e6f7ff',
                borderRadius: '24px',
                border: '1px solid #91d5ff',
              }}
            >
              <CheckmarkFilled size={20} style={{ color: '#1890ff' }} />
              <span style={{ color: '#1890ff', fontWeight: 500 }}>Special Educational Discounts Available</span>
            </div>
          </div>
        </Column>

        {/* Pricing Cards */}
        {sortedPlans.map((plan, index) => (
          <Column key={plan.id} lg={5} md={8} sm={4} style={{ marginBottom: '2rem' }}>
            <Tile
              style={{
                padding: '2rem',
                height: '100%',
                cursor: 'pointer',
                backgroundColor: plan.highlight ? '#f0f9ff' : '#ffffff',
                border: plan.highlight ? '2px solid #1890ff' : '1px solid #e0e0e0',
                borderRadius: '8px',
                position: 'relative',
                transition: 'all 0.3s ease',
              }}
              onClick={() => onPricingInteraction(plan.id)}
            >
              {/* Popular/Recommended Badge */}
              {plan.highlight && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1890ff',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {variant === 'educator' && 'TEACHER FAVORITE'}
                  {variant === 'parent' && 'MOST POPULAR'}
                  {variant === 'administrator' && 'RECOMMENDED'}
                  {variant === 'default' && 'MOST POPULAR'}
                </div>
              )}

              <Stack gap={4}>
                {/* Plan Header */}
                <div>
                  <Heading
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      color: '#161616',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {plan.name}
                  </Heading>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#525252',
                      lineHeight: 1.4,
                    }}
                  >
                    {plan.description}
                  </p>
                </div>

                {/* Pricing */}
                <div>
                  {plan.price.monthly > 0 ? (
                    <>
                      <div
                        style={{
                          fontSize: '3rem',
                          fontWeight: 300,
                          color: '#161616',
                          lineHeight: 1,
                        }}
                      >
                        ${plan.price.annual > 0 ? plan.price.annual : plan.price.monthly}
                        <span
                          style={{
                            fontSize: '1rem',
                            color: '#525252',
                            fontWeight: 400,
                          }}
                        >
                          {plan.price.annual > 0 ? '/year' : '/month'}
                        </span>
                      </div>
                      {plan.price.perStudent && (
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: '#525252',
                            marginTop: '0.5rem',
                          }}
                        >
                          ${plan.price.perStudent}/student/month
                        </div>
                      )}
                      {plan.educationalDiscount && (
                        <Tag type="green" size="sm" style={{ marginTop: '0.5rem' }}>
                          {plan.educationalDiscount}% Educational Discount
                        </Tag>
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: 600,
                        color: '#161616',
                        marginBottom: '0.5rem',
                      }}
                    >
                      Custom Pricing
                    </div>
                  )}
                </div>

                {/* Features List */}
                <Stack gap={2}>
                  {plan.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                      }}
                    >
                      <CheckmarkFilled
                        size={16}
                        style={{
                          color: '#24a148',
                          marginTop: '0.125rem',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: '0.875rem',
                          color: '#161616',
                          lineHeight: 1.4,
                        }}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </Stack>

                {/* CTA Button */}
                <Button
                  kind={plan.highlight ? 'primary' : 'secondary'}
                  size="lg"
                  renderIcon={ArrowRight}
                  style={{
                    width: '100%',
                    marginTop: '1rem',
                    backgroundColor: plan.highlight ? '#1890ff' : undefined,
                    borderColor: plan.highlight ? '#1890ff' : undefined,
                  }}
                  onClick={() => onPricingInteraction(plan.id)}
                >
                  {plan.price.monthly > 0 ? `Start ${plan.trialLength}-Day Free Trial` : 'Contact Sales'}
                </Button>

                {/* Best For */}
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: plan.highlight ? '#ffffff' : '#f4f4f4',
                    borderRadius: '4px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#525252',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    Best For:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {plan.bestFor.map((item, itemIndex) => (
                      <Tag key={itemIndex} type="outline" size="sm">
                        {item}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Stack>
            </Tile>
          </Column>
        ))}

        {/* Additional Information */}
        <Column lg={16} md={8} sm={4}>
          <div
            style={{
              textAlign: 'center',
              marginTop: '3rem',
              padding: '2rem',
              backgroundColor: '#f4f4f4',
              borderRadius: '8px',
            }}
          >
            <p
              style={{
                fontSize: '1rem',
                color: '#525252',
                marginBottom: '1rem',
              }}
            >
              All plans include free onboarding, training, and migration assistance.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
                <span style={{ fontSize: '0.875rem', color: '#525252' }}>No Setup Fees</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
                <span style={{ fontSize: '0.875rem', color: '#525252' }}>Cancel Anytime</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
                <span style={{ fontSize: '0.875rem', color: '#525252' }}>Educational Discounts</span>
              </div>
            </div>
          </div>
        </Column>
      </Grid>
    </section>
  );
};

export default PricingSection;
